
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/auth-context";
import { Agent } from "@/models/agent";
import { AgentService } from "@/services/agent-service";
import { Button } from "@/components/ui/button";
import { CirclePlus, Package, PlusCircle, ArrowLeft } from "lucide-react";
import AgentTable from "@/components/agent-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const MyAgents: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const agentsData = await AgentService.getAgents();
        setAgents(agentsData);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch agents:", error);
        setLoading(false);
      }
    };
    
    fetchAgents();
  }, []);
  
  // Redirect if user is not logged in or not a creator
  if (!user) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-semibold mb-4">Creator Access Required</h2>
        <p className="mb-4">You need to be logged in as a creator to view your agents.</p>
        <div className="flex justify-center gap-4">
          <Button onClick={() => navigate("/login")}>
            Sign In
          </Button>
          <Button variant="outline" onClick={() => navigate("/register")}>
            Register
          </Button>
        </div>
      </div>
    );
  }
  
  if (user.profileType !== "creator") {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-semibold mb-4">Creator Access Required</h2>
        <p className="mb-4">Only creators can manage agents. Your account is registered as a buyer.</p>
        <Button variant="outline" onClick={() => navigate("/")}>
          Go Back
        </Button>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>My Agents - OGMAI</title>
      </Helmet>
      <div className="container py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2" 
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold">My Agents</h1>
            <p className="text-muted-foreground mt-1">
              Manage your AI agents and track their performance
            </p>
          </div>
        </div>
        
        <div className="flex justify-end mb-6">
          <Button className="flex items-center gap-2" onClick={() => navigate("/create-agent")}>
            <PlusCircle className="h-4 w-4" />
            New Agent
          </Button>
        </div>
        
        {loading ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading your agents...</p>
              </div>
            </CardContent>
          </Card>
        ) : agents.length === 0 ? (
          <Card>
            <CardContent className="py-12 px-6">
              <div className="text-center border-2 border-dashed rounded-lg p-8">
                <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-semibold mb-2">No agents yet</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Create your first AI agent to share your expertise with the world
                  and start generating passive income.
                </p>
                <Button className="flex items-center gap-2" onClick={() => navigate("/create-agent")}>
                  <CirclePlus className="h-4 w-4" />
                  Create Your First Agent
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>My AI Agents</CardTitle>
              <CardDescription>All the agents you've created and their current status</CardDescription>
            </CardHeader>
            <CardContent>
              <AgentTable agents={agents} />
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default MyAgents;
